import { Chip } from '@kerfjs/ui/chip';
import { flex, rem } from '@kerfjs/ui/css-values';
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { Row } from '@kerfjs/ui/row';
import { Spacer } from '@kerfjs/ui/spacer';
import { PopupSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';
import { MoreHorizontal } from 'lucide';

import { materializedRowMenus } from '../data/state.js';
import { LineItemActions } from './line-item-actions.js';

export function DemandLineItemMain({
  demandId,
  lineId,
  lineNumber,
  title,
  quantity,
  status,
  currentAction,
  readOnly,
  canDecide = true,
  decisionsLocked = readOnly,
}: {
  demandId?: string;
  lineId: string;
  lineNumber: number;
  title: string;
  quantity: string;
  status: SafeHtml;
  currentAction?: string | null;
  readOnly: boolean;
  canDecide?: boolean;
  /** Decisions stay open on a decided demand whose lines are locked (HS2-Y50H8E). */
  decisionsLocked?: boolean;
}): SafeHtml {
  // A locked line keeps its menu while it can still be decided, minus Remove.
  const decidable = canDecide && !decisionsLocked;
  const actionLabel = `Actions for Line ${String(lineNumber)}`;
  const opened = materializedRowMenus.value;
  // Rows outside a demand (catalog specimens) have no opener wiring, so they render the full menu.
  const rowMenuLive = demandId === undefined || (opened.demandId === demandId && opened.lineIds.has(lineId));
  return (
    <Row className="demand-line-item-main" vAlign="middle" wrap controlInsets="rl">
      {/* A fixed number column and a shrinking title, configured on Rows (HS2-1PQQ6Y). */}
      <Row flex={flex(0, 0, rem(1.5))}>
        <Text variant="span" tone="quiet" size="compact" font="monospace">
          {lineNumber}
        </Text>
      </Row>
      {/* The quantity and unit follow the title inline, wrapping under it only when the
          title runs out of room (HS2-CRHTGR). */}
      <Row flex={flex(1, 1, rem(10))} gap="xs" vAlign="middle" wrap>
        <Text variant="span" className="demand-line-item-title">
          {title}
        </Text>
        {quantity !== '' ? (
          <Chip size="compact" tone="neutral" appearance="outline">
            {quantity}
          </Chip>
        ) : null}
      </Row>

      <Spacer flex />
      {/* Status markers sit on the trailing side, beside the row menu (HS2-P1YNNS). */}
      {status}
      {readOnly && !decidable ? null : (
        <PopupSurface>
          <LineItemActions
            demandId={demandId}
            lineId={lineId}
            currentAction={currentAction}
            canDecide={decidable}
            canRemove={!readOnly}
            lazy={!rowMenuLive}
            trigger={{
              label: actionLabel,
              icon: <LucideIcon icon={MoreHorizontal} name="" />,
              // A lazy menu carries its line id so the first open can fill it in (HS2-D2G57R).
              rootAttributes: rowMenuLive
                ? { 'data-row-menu': '' }
                : { 'data-row-menu': '', 'data-row-menu-lazy': lineId, 'data-row-menu-demand': demandId },
            }}
          />
        </PopupSurface>
      )}
    </Row>
  );
}
